/**
 * RequireAuth（03 3.2.2・3.2.4）：`['auth','me']`（API-AUTH-05）を読み、未ログインなら /login?redirect=<今のパス＋クエリ> へ replace で移る。
 * ページを移るたびに /auth/me を読み直す（ロールの変更を次の画面から反映：要件書 NFR-16）。読み直しの間も前の値で画面を出し続ける。
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isApiError } from '../../api/http-error';
import { ErrorPanel } from '../../components/feedback/ErrorPanel';
import { PageLoading } from '../../components/feedback/PageLoading';
import { ME_QUERY_KEY, useMe } from '../../features/auth/queries';
import { errorMessageFor, traceIdOf } from '../../lib/messages';

const LOGIN_REQUIRED_CODES = new Set(['AUTH_002', 'AUTH_004', 'AUTH_006']);

export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const query_client = useQueryClient();
  const me = useMe();
  const first_path = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== first_path.current) {
      first_path.current = location.pathname;
      void query_client.invalidateQueries({ queryKey: ME_QUERY_KEY });
    }
  }, [location.pathname, query_client]);

  if (me.data) {
    return <>{children}</>;
  }
  if (me.isPending) {
    return <PageLoading />;
  }
  if (isApiError(me.error) && (me.error.status === 401 || LOGIN_REQUIRED_CODES.has(me.error.code))) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }
  return (
    <ErrorPanel message={errorMessageFor(me.error)} trace_id={traceIdOf(me.error)} onRetry={() => void me.refetch()} />
  );
}
